import { SetStateAction, Suspense, useState } from "react";
import { Output } from "valibot";

import IntegrationButton from "components/ChatPanel/Integrations/IntegrationButton";
import AgentHandoffFormFields from "components/Integration/AgentHandoffFormFields";
import IntegrationFormFields from "components/Integration/IntegrationFormFields";
import LoadingPlaceholder from "components/LoadingPlaceholder";
import MessageWithLinks from "components/MessageWithLinks";
import {
  BotInfoSchema,
  ChatDataSchema,
  ChatMessageTypeSchema,
  IntegrationOptionsSchema,
  IntegrationSchema,
} from "components/SonicBot/schemas";
import { saveLikeDislike } from "shared/api";
import { useConfigContext } from "shared/hooks";
import { AppConfiguration, Literals, Source } from "shared/types";
import classNames from "shared/utils/classNames";

import AgentInfoMessage from "./AgentInfoMessage";
import JumpingDots from "./JumpingDots";
import LikeButton from "./LikeButton";
import MessageAvatar from "./MessageAvatar";
import Sources from "./Sources";

import "./index.css";

type Props = {
  id: string;
  botInfo: Output<typeof BotInfoSchema>;
  isSent: boolean;
  message: string;
  sources?: Source[];
  imageUrls?: string[];
  config: AppConfiguration;
  chatId: string;
  chatData: Output<typeof ChatDataSchema>;
  setChatData: React.Dispatch<SetStateAction<any[]>>;
  isLastMessage?: boolean;
  copyTexts: Literals;
  showLikeDislikeBtn?: boolean;
  chatMessageType?: Output<typeof ChatMessageTypeSchema>;
  avatarUrl?: string;
  integration?: Output<typeof IntegrationSchema>;
  likeStatus?: string;
  onIntegrationClick?: (
    integration: Output<typeof IntegrationOptionsSchema>,
  ) => Promise<void>;
  token?: string;
  handleSendIntegrationData?: ({
    configType,
    integrationData,
  }: {
    configType: string;
    integrationData: any;
  }) => Promise<void>;
};

function MessageBubble(props: Props) {
  const { isIframe } = useConfigContext();

  const {
    id,
    botInfo,
    isSent,
    message,
    sources,
    config,
    chatId,
    chatData,
    setChatData,
    isLastMessage,
    copyTexts,
    showLikeDislikeBtn = true,
    imageUrls,
    chatMessageType,
    avatarUrl,
    integration,
    likeStatus: originalLikeStatus = "",
    onIntegrationClick,
    handleSendIntegrationData,
  } = props;

  const [likeStatus, setLikeStatus] = useState<string>(originalLikeStatus);

  const handleLikeDislike = (status: string) => {
    setLikeStatus(status);
    setChatData(
      chatData.map(item => {
        if (item.id === id) {
          return { ...item, like_status: status };
        }
        return item;
      }),
    );

    saveLikeDislike({
      token: config.token,
      chatId,
      messageId: id,
      likeStatus: status,
    });
  };

  const generationInProgress = isSent === false && !message?.trim();
  const showJumpingDots =
    generationInProgress || id === "jumping-dots-container";

  if (
    ["waiting_for_live_agent", "handoff_canceled"].includes(
      chatMessageType ?? "",
    )
  ) {
    return null;
  }

  if (chatMessageType === "live_agent_info") {
    return <AgentInfoMessage message={message} />;
  }

  return (
    <div id={id} className={classNames("likes-container", isSent && "sent")}>
      <div className="hidden">
        <Suspense fallback="">
          <MessageWithLinks message="" isSent={isSent} />
        </Suspense>
      </div>
      <div
        className={classNames(
          "flex max-w-full gap-2",
          isIframe && "items-end rounded-lg p-4",
        )}
      >
        <MessageAvatar
          isSent={isSent}
          isIframe={isIframe}
          botAvatar={avatarUrl ?? botInfo.bot_info_config.chatbot_avatar}
          isLastMessage={isLastMessage}
        />
        <div className={classNames("min-w-0", isSent && "ml-[60px]")}>
          <div>
            {imageUrls?.map((url, index) => {
              return (
                <img
                  key={index}
                  src={url}
                  alt="message"
                  width={250}
                  height={250}
                />
              );
            })}

            {showJumpingDots ? (
              <JumpingDots />
            ) : (
              <Suspense
                fallback={
                  <LoadingPlaceholder
                    generationLanguage={
                      botInfo.bot_prompt_config.generation_language
                    }
                  />
                }
              >
                <MessageWithLinks message={message} isSent={isSent} />
              </Suspense>
            )}

            {integration?.available?.map(integration => {
              return (
                <div className="mt-2 w-full">
                  <IntegrationButton
                    integration={integration}
                    onIntegrationClick={() => onIntegrationClick?.(integration)}
                    isLastMessage={!!isLastMessage}
                  />
                </div>
              );
            })}

            {integration?.user_integration_data?.submitted_user_fields ? (
              <IntegrationFormFields
                formFields={
                  integration.user_integration_data.submitted_user_fields
                }
                generationLanguage={
                  botInfo.bot_prompt_config.generation_language
                }
              />
            ) : (
              handleSendIntegrationData &&
              integration?.integration_info?.required_user_fields &&
              !integration?.user_integration_data?.submitted_user_fields && (
                <AgentHandoffFormFields
                  key={id}
                  integration={integration.integration_info}
                  generationLanguage={
                    botInfo.bot_prompt_config.generation_language
                  }
                  handleSendIntegrationData={handleSendIntegrationData}
                />
              )
            )}

            {botInfo.bot_info_config.is_show_sources && !isSent && (
              <Sources sources={sources} copyTexts={copyTexts} />
            )}
          </div>
        </div>
      </div>
      {!isSent && id !== "-1" && showLikeDislikeBtn && (
        <div
          className={classNames(
            "flex w-full items-end justify-end gap-2",
            isIframe ? "p-2 pt-0" : "pt-2",
          )}
        >
          <LikeButton
            handleLikeDislike={handleLikeDislike}
            likeStatusType="liked"
            likeStatus={likeStatus}
          />
          <LikeButton
            handleLikeDislike={handleLikeDislike}
            likeStatusType="disliked"
            likeStatus={likeStatus}
          />
        </div>
      )}
    </div>
  );
}

export default MessageBubble;
